import { authenticatedFetch, ForbiddenError, UnauthorizedError } from "@src/features/auth/services/authenticated-fetch";
import { getSelectedTenantRealm } from "@src/features/tenants";
import { getEnvironment } from "@src/config/environment-store";
import { track } from "@visioni/a3s-analytics";
import { ApiErrorEvent, ApiRequestCompleted } from "./analytics";

interface ApiRequestOptions {
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  path: string;
  body?: unknown;
  params?: Record<string, string | number | undefined>;
  logMessage?: string;
  timeout?: number; // Timeout in milliseconds (default: 30000 = 30 seconds)
}

export class ApiError extends Error {
  readonly kind = "http" as const;

  constructor(
    public status: number,
    public statusText: string,
    public body?: string,
  ) {
    super(`HTTP error! status: ${status}`);
    this.name = "ApiError";
  }
}

export class ApiTimeoutError extends Error {
  readonly kind = "timeout" as const;

  constructor(
    public url: string,
    public timeoutMs: number,
  ) {
    super(`Request timeout after ${timeoutMs}ms: ${url}`);
    this.name = "ApiTimeoutError";
  }
}

function trackApiTiming(url: string, method: string, durationMs: number, statusCode: number): void {
  try {
    const path = new URL(url).pathname;
    track(ApiRequestCompleted, {
      method,
      path,
      status_code: statusCode,
      duration_ms: durationMs,
    });
  } catch {
    // Don't let analytics tracking break the app
  }
}

type ApiErrorType = "http_error" | "timeout" | "network_error" | "unknown";

/**
 * Classifies a failure that `apiRequest` did not track where it happened. Auth errors from
 * `authenticatedFetch` are HTTP outcomes. Only a `TypeError` from the fetch call itself is a
 * network failure: a `TypeError` thrown later is a bug or a malformed body, not the network.
 */
function untrackedFailure(
  error: unknown,
  duringFetch: boolean,
): { errorType: ApiErrorType; statusCode: number | undefined } {
  if (error instanceof UnauthorizedError) return { errorType: "http_error", statusCode: 401 };
  if (error instanceof ForbiddenError) return { errorType: "http_error", statusCode: 403 };
  if (duringFetch && error instanceof TypeError) return { errorType: "network_error", statusCode: undefined };
  return { errorType: "unknown", statusCode: undefined };
}

function trackApiError(
  url: string,
  method: string,
  errorType: ApiErrorType,
  statusCode: number | undefined,
  durationMs: number,
): void {
  try {
    const path = new URL(url).pathname;
    track(ApiErrorEvent, {
      error_type: errorType,
      method,
      path,
      status_code: statusCode,
      duration_ms: durationMs,
    });
  } catch {
    // Don't let analytics tracking break the app
  }
}

export async function apiRequest<T>(options: ApiRequestOptions): Promise<T> {
  const { method, path, body, params, logMessage, timeout = 30000 } = options;

  const baseUrl = await getResolvedApiBaseUrl();
  const url = new URL(`${baseUrl}${path}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    });
  }

  const urlString = url.toString();

  if (logMessage) {
    console.log(logMessage, urlString);
  }

  // Create AbortController for timeout
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => {
    console.error(`[API Request] Timeout after ${timeout}ms:`, urlString);
    abortController.abort();
  }, timeout);

  const startTime = Date.now();
  // Each request's outcome is tracked exactly once: set once `trackApiError` or
  // `trackApiTiming` has run, so the catch rethrows a later failure without tracking it.
  let outcomeTracked = false;
  // True until the fetch call settles; only a failure of the fetch itself can be a network error.
  let duringFetch = true;

  try {
    const fetchOptions: RequestInit = {
      method,
      signal: abortController.signal,
    };

    if (body !== undefined) {
      fetchOptions.headers = { "Content-Type": "application/json" };
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await authenticatedFetch(urlString, fetchOptions);
    duringFetch = false;

    const duration = Date.now() - startTime;

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Tracked before reading the body, so a failed read still counts as this HTTP error.
      trackApiError(urlString, method, "http_error", response.status, duration);
      outcomeTracked = true;

      const errorText = await response.text();
      console.error(`[API Request] Error ${response.status} (${duration}ms):`, errorText);

      // Normalize 403s into a shared ForbiddenError so screens using
      // DataStateView can render a consistent access-denied experience.
      if (response.status === 403) {
        throw new ForbiddenError("Access denied - insufficient permissions");
      }

      throw new ApiError(response.status, response.statusText, errorText);
    }

    trackApiTiming(urlString, method, duration, response.status);
    outcomeTracked = true;

    const data = (await response.json()) as T;
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    const duration = Date.now() - startTime;

    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof Error && error.name === "AbortError") {
      console.error(`[API Request] Request aborted (timeout) after ${duration}ms:`, urlString);
      if (!outcomeTracked) trackApiError(urlString, method, "timeout", undefined, duration);
      throw new ApiTimeoutError(urlString, timeout);
    }

    console.error(`[API Request] Unexpected error for request to ${urlString} after ${duration}ms:`, error);
    if (!outcomeTracked) {
      const { errorType, statusCode } = untrackedFailure(error, duringFetch);
      trackApiError(urlString, method, errorType, statusCode, duration);
    }
    throw error;
  }
}

export async function getResolvedApiBaseUrl(): Promise<string> {
  const tenantRealm = getSelectedTenantRealm();
  // TODO: Just use the tenant schema TenantInfo schema from a3s-dir instead of redefining in the app.
  //   Right now, this returns 'https://{realmfqdn}' as string. (Not the name nor the api URL for the realm.)
  if (tenantRealm) return tenantRealm;
  // TODO: Force this path to be impossible by construction.
  //   That is, you can't even use this module unless you're able to construct it with a TenantInfo.
  //   For now, we'll try R01. Good luck everyone.
  const env = await getEnvironment();
  console.warn(
    "[DEPRECATED] No tenant was selected. API requests should not be made without resolving a tenant to a realm first.",
  );
  return `https://r01.${env.fqdn}`;
}
